// RAPIFAST :: "Documentos y verificación" del negocio aliado (DEMO, solo navegador).
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Eye, Trash2, Ban, CheckCircle2, FileCheck2 } from "lucide-react";
import { PageHeader } from "@/components/kit";
import { DemoBanner, Badge, SemaforoBadge, Field, SectionCard, ConfidentialDocModal } from "@/components/verificationKit";
import { useVerificationDemo, fileToDemoRecord } from "@/context/VerificationDemoContext";
import { SELF_BUSINESS_ID, VERIF_STATUS, businessSemaforo } from "@/lib/verificationMock";

export default function BusinessDocuments() {
  const { selfBusiness, updateBusiness } = useVerificationDemo();
  const b = selfBusiness();
  const fileRef = useRef(null);
  const [view, setView] = useState(null);

  const addSupport = async (file) => {
    const rec = await fileToDemoRecord(file);
    const supports = [...(b.supports || []), { id: crypto.randomUUID(), label: file.name, ...rec }];
    // Adjuntar un soporte reactiva la revisión; NO aprueba nada automáticamente.
    updateBusiness(SELF_BUSINESS_ID, { supports, status: b.status === "REJECTED" ? "IN_REVIEW" : b.status });
    toast.success("Soporte de prueba adjuntado. Queda sujeto a revisión del administrador.");
  };
  const removeSupport = (id) =>
    updateBusiness(SELF_BUSINESS_ID, { supports: (b.supports || []).filter((s) => s.id !== id) });

  const toggleAgreement = () =>
    updateBusiness(SELF_BUSINESS_ID, {
      agreementAccepted: !b.agreementAccepted,
      agreementAcceptedAt: !b.agreementAccepted ? new Date().toISOString().slice(0, 10) : "",
    });

  const resubmit = () => {
    updateBusiness(SELF_BUSINESS_ID, { status: "IN_REVIEW", reviewNotes: "" });
    toast.success("Registro vuelto a presentar. Queda en revisión.");
  };

  return (
    <div data-testid="business-documents-page">
      <PageHeader
        title="Documentos y verificación"
        subtitle="Revisa el estado de tu vinculación y adjunta los soportes que te solicite el administrador."
        testId="business-documents-header"
      />
      <DemoBanner />

      <div
        className="mb-5 flex flex-wrap items-center gap-2 rounded-2xl border border-[#27272A] bg-[#141416] px-4 py-3"
        data-testid="business-status-banner"
      >
        <span className="text-sm font-bold text-gray-300">Estado de tu vinculación:</span>
        <Badge tone={VERIF_STATUS[b.status]} testId="business-self-status">{VERIF_STATUS[b.status].label}</Badge>
        <SemaforoBadge level={businessSemaforo(b)} testId="business-self-semaforo" />
        {b.reviewedAt && <span className="text-xs text-gray-500">Revisado {b.reviewedAt}</span>}
      </div>

      {b.status === "REJECTED" && (
        <div className="mb-5 rounded-2xl border border-red-500/50 bg-red-500/10 px-4 py-3" data-testid="business-rejected-box">
          <div className="flex items-center gap-2 text-sm font-bold text-red-400"><Ban size={16} /> Registro rechazado</div>
          {b.reviewNotes && <p className="mt-1 text-xs text-red-300">Motivo: {b.reviewNotes}</p>}
          <button
            onClick={resubmit}
            data-testid="business-resubmit-button"
            className="mt-3 rounded-xl bg-[#FF6A00]/15 px-4 py-2 text-sm font-bold text-[#FF6A00] hover:bg-[#FF6A00]/25"
          >
            Corregir y volver a presentar
          </button>
        </div>
      )}

      <div className="space-y-4">
        <SectionCard title="Datos registrados del negocio" testId="business-self-data">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Nombre comercial" value={b.commercialName} />
            <Field label="NIT / identificación" value={b.nit} sensitive />
            <Field label="Actividad" value={b.activityType} />
            <Field label="Municipio" value={b.municipality} />
            <Field label="Teléfono" value={b.phone} />
            <Field label="Correo" value={b.email} />
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Los datos operativos y las verificaciones internas los completa el administrador durante la revisión.
          </p>
        </SectionCard>

        <SectionCard title="Acuerdo de vinculación" testId="business-agreement-card">
          <label className="flex cursor-pointer items-start gap-2.5 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={b.agreementAccepted}
              onChange={toggleAgreement}
              className="mt-0.5 accent-[#FF6A00]"
              data-testid="business-agreement-checkbox"
            />
            Acepto el Acuerdo de Vinculación de Negocios Aliados (borrador v1.0, pendiente de aprobación
            jurídica). Entiendo que RAPIFAST gestiona entregas de productos y paquetes, no transporte de pasajeros.
          </label>
          {b.agreementAccepted && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-400" data-testid="business-agreement-accepted">
              <FileCheck2 size={13} /> Aceptado el {b.agreementAcceptedAt} (demo). El acuerdo sigue siendo borrador.
            </div>
          )}
        </SectionCard>

        <SectionCard title="Soportes adjuntos (opcionales)" testId="business-supports-card">
          <p className="mb-3 text-xs text-gray-500">
            Adjunta solo los soportes que el administrador te solicite. No existe un listado documental
            obligatorio definido para negocios; no se inventan requisitos.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => e.target.files[0] && addSupport(e.target.files[0])}
            data-testid="business-support-file-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="business-support-upload-button"
            className="flex items-center gap-1.5 rounded-lg bg-[#FF6A00]/15 px-3 py-1.5 text-xs font-bold text-[#FF6A00] hover:bg-[#FF6A00]/25"
          >
            <Upload size={13} /> Adjuntar soporte de prueba
          </button>
          <div className="mt-3 space-y-2" data-testid="business-supports-list">
            {(b.supports || []).length === 0 && (
              <div className="text-xs text-gray-600">Sin soportes adjuntos.</div>
            )}
            {(b.supports || []).map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-[#1e1e21] bg-[#101012] px-3 py-2.5" data-testid={`business-support-${s.id}`}>
                <div className="min-w-[160px] grow text-sm text-gray-200">
                  {s.label}
                  <div className="text-xs text-gray-500">Cargado {new Date(s.uploadedAt).toLocaleDateString("es-CO")} · pendiente de revisión</div>
                </div>
                <button onClick={() => setView(s)} data-testid={`business-support-view-${s.id}`}
                  className="flex items-center gap-1 rounded-lg border border-[#333] px-2.5 py-1.5 text-xs font-bold text-gray-300 hover:bg-[#1F1F1F]">
                  <Eye size={13} /> Ver
                </button>
                <button onClick={() => removeSupport(s.id)} data-testid={`business-support-remove-${s.id}`}
                  className="flex items-center gap-1 rounded-lg border border-[#333] px-2.5 py-1.5 text-xs font-bold text-red-400 hover:bg-red-500/10">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </SectionCard>

        <div className="flex items-center gap-2 text-xs text-gray-500" data-testid="business-review-hint">
          {b.status === "APPROVED" ? <CheckCircle2 size={14} className="text-emerald-400" /> : null}
          El administrador revisa tus datos, el acuerdo y los soportes antes de aprobar la vinculación.
        </div>
      </div>

      {view && (
        <ConfidentialDocModal docLabel={view.label} ownerName="Soporte del negocio (demo)" file={view} onClose={() => setView(null)} />
      )}
    </div>
  );
}
